import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, List, Check, Trash2, Edit, Scissors, Clock, User, FileText, ExternalLink, Ban } from 'lucide-react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Select from '@/components/ui/Select';
import GroomingAppointmentForm from '@/components/GroomingAppointmentForm';
import { useGroomingCalendar, useCompleteGroomingAppointment, useDeleteGroomingAppointment, useCancelGroomingAppointment } from '@/hooks/useGrooming';
import { GroomingAppointment, GroomingStatus } from '@/types';
import { SERVICE_TYPE_LABELS, STATUS_LABELS, STATUS_COLORS } from '@/services/grooming';
import { formatPrice } from '@/utils/format';
import { useAuthStore } from '@/context/authStore';

type ViewMode = 'slots' | 'list';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
];

// Configuración del calendario de slots
const SLOT_DURATION = 30; // minutos

// Generar slots de tiempo según el horario configurado por la veterinaria
const generateTimeSlots = (startHour: number, endHour: number) => {
  const slots: string[] = [];
  const start = Math.min(Math.max(0, startHour), 23);
  const end = Math.min(Math.max(start + 1, endHour), 24);
  for (let hour = start; hour < end; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

export default function Grooming() {
  const navigate = useNavigate();
  const { tenant } = useAuthStore();
  // Horario del calendario configurable por veterinaria (default 8-22)
  const TIME_SLOTS = useMemo(
    () => generateTimeSlots(tenant?.calendar_start_hour ?? 8, tenant?.calendar_end_hour ?? 22),
    [tenant?.calendar_start_hour, tenant?.calendar_end_hour]
  );
  const [viewMode, setViewMode] = useState<ViewMode>('slots');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState<GroomingStatus | ''>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<GroomingAppointment | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<GroomingAppointment | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<GroomingAppointment | null>(null);
  const [completeConfirm, setCompleteConfirm] = useState<GroomingAppointment | null>(null);
  const [completeResult, setCompleteResult] = useState<{ appointment: GroomingAppointment; serviceOrderId: number; total: number } | null>(null);

  // Calcular rango de fechas según la vista
  const { dateFrom, dateTo, days } = useMemo(() => {
    const from = new Date(currentDate);
    const to = new Date(currentDate);

    if (viewMode === 'slots') {
      // Obtener inicio de semana (lunes)
      const day = from.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      from.setDate(from.getDate() + diff);
      to.setDate(from.getDate() + 6);
    } else if (viewMode === 'list') {
      // Para lista, mostrar mes actual
      from.setDate(1);
      to.setMonth(to.getMonth() + 1);
      to.setDate(0);
    }

    const daysArray: Date[] = [];
    const current = new Date(from);
    while (current <= to) {
      daysArray.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return {
      dateFrom: from.toISOString().split('T')[0],
      dateTo: to.toISOString().split('T')[0],
      days: daysArray,
    };
  }, [currentDate, viewMode]);

  const { data: appointments = [], isLoading } = useGroomingCalendar(dateFrom, dateTo);
  const completeAppointment = useCompleteGroomingAppointment();
  const deleteAppointment = useDeleteGroomingAppointment();
  const cancelAppointment = useCancelGroomingAppointment();

  // Filtrar por estado si está seleccionado
  const filteredAppointments = useMemo(() => {
    if (!statusFilter) return appointments;
    return appointments.filter((apt) => apt.status === statusFilter);
  }, [appointments, statusFilter]);

  // Agrupar turnos por fecha
  const appointmentsByDate = useMemo(() => {
    const grouped: Record<string, GroomingAppointment[]> = {};
    filteredAppointments.forEach((apt) => {
      if (!grouped[apt.scheduled_date]) {
        grouped[apt.scheduled_date] = [];
      }
      grouped[apt.scheduled_date].push(apt);
    });
    // Ordenar por hora dentro de cada día
    Object.values(grouped).forEach((list) => {
      list.sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
    });
    return grouped;
  }, [filteredAppointments]);

  // Función para verificar si un slot está ocupado y obtener el turno
  const getAppointmentAtSlot = (dateStr: string, time: string): GroomingAppointment | null => {
    const dayAppointments = appointmentsByDate[dateStr] || [];
    const slotMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);

    for (const apt of dayAppointments) {
      if (apt.status === 'cancelled') continue;

      const aptTime = apt.scheduled_time.slice(0, 5);
      const aptMinutes = parseInt(aptTime.split(':')[0]) * 60 + parseInt(aptTime.split(':')[1]);
      const aptEndMinutes = aptMinutes + apt.duration_minutes;

      // El slot está dentro del rango del turno
      if (slotMinutes >= aptMinutes && slotMinutes < aptEndMinutes) {
        return apt;
      }
    }
    return null;
  };

  // Verificar si un slot es el inicio de un turno
  const isAppointmentStart = (dateStr: string, time: string): boolean => {
    const dayAppointments = appointmentsByDate[dateStr] || [];
    return dayAppointments.some(apt =>
      apt.scheduled_time.slice(0, 5) === time && apt.status !== 'cancelled'
    );
  };

  // Calcular cuántos slots ocupa un turno
  const getAppointmentSlotSpan = (apt: GroomingAppointment): number => {
    return Math.ceil(apt.duration_minutes / SLOT_DURATION);
  };

  const navigateCalendar = (direction: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'slots') {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else if (viewMode === 'list') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDateShort = (date: Date) => {
    return date.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric' });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastSlot = (date: Date, time: string) => {
    const now = new Date();
    const slotDate = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    slotDate.setHours(hours, minutes, 0, 0);
    return slotDate < now;
  };

  const handleNewAppointment = (date?: string, time?: string) => {
    setEditingAppointment(null);
    setSelectedDate(date || null);
    setSelectedTime(time || null);
    setIsModalOpen(true);
  };

  const handleEditAppointment = (apt: GroomingAppointment) => {
    setEditingAppointment(apt);
    setSelectedDate(null);
    setSelectedTime(null);
    setIsModalOpen(true);
  };

  const handleComplete = async () => {
    if (!completeConfirm) return;
    try {
      const result = await completeAppointment.mutateAsync(completeConfirm.id);
      setCompleteConfirm(null);
      // Mostrar modal con info de la comanda creada
      setCompleteResult({
        appointment: result.appointment,
        serviceOrderId: result.service_order_id,
        total: result.service_order_total,
      });
    } catch (error) {
      console.error('Error completing:', error);
    }
  };

  const handleGoToServiceOrder = () => {
    if (completeResult) {
      // Navegar a comandas con la comanda seleccionada para editar
      navigate(`/dashboard/service-orders?edit=${completeResult.serviceOrderId}`);
      setCompleteResult(null);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteAppointment.mutateAsync(deleteConfirm.id);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
  };

  const handleCancel = async () => {
    if (cancelConfirm) {
      try {
        await cancelAppointment.mutateAsync(cancelConfirm.id);
        setCancelConfirm(null);
      } catch (error) {
        console.error('Error cancelling:', error);
      }
    }
  };

  // Calcular qué slots están cubiertos por rowSpan de turnos anteriores
  const getCoveredSlots = useMemo(() => {
    const covered: Record<string, Set<string>> = {};

    days.forEach((day) => {
      const dateStr = day.toISOString().split('T')[0];
      covered[dateStr] = new Set();

      const dayAppointments = appointmentsByDate[dateStr] || [];
      dayAppointments.forEach((apt) => {
        if (apt.status === 'cancelled') return;

        const aptTime = apt.scheduled_time.slice(0, 5);
        const [aptHour, aptMin] = aptTime.split(':').map(Number);
        const aptStartMinutes = aptHour * 60 + aptMin;
        const slotsNeeded = Math.ceil(apt.duration_minutes / SLOT_DURATION);

        // Marcar todos los slots excepto el primero como cubiertos
        for (let i = 1; i < slotsNeeded; i++) {
          const coveredMinutes = aptStartMinutes + i * SLOT_DURATION;
          const coveredHour = Math.floor(coveredMinutes / 60);
          const coveredMin = coveredMinutes % 60;
          const coveredTime = `${coveredHour.toString().padStart(2, '0')}:${coveredMin.toString().padStart(2, '0')}`;
          covered[dateStr].add(coveredTime);
        }
      });
    });

    return covered;
  }, [days, appointmentsByDate]);

  // Renderizar vista de slots (nueva)
  const renderSlotsView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 bg-gray-50 z-10 w-16 px-2 py-3 text-xs font-medium text-gray-500 border-b border-r">
                Hora
              </th>
              {days.map((day) => {
                const dateStr = day.toISOString().split('T')[0];
                return (
                  <th
                    key={dateStr}
                    className={`min-w-[140px] px-2 py-3 text-xs font-medium border-b ${
                      isToday(day) ? 'bg-blue-100 text-blue-700' : 'text-gray-500'
                    }`}
                  >
                    {formatDateShort(day)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {TIME_SLOTS.map((time, timeIndex) => (
              <tr key={time} className={timeIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                <td className="sticky left-0 bg-inherit z-10 px-2 py-0 text-xs text-gray-500 border-r font-medium text-center">
                  {time}
                </td>
                {days.map((day) => {
                  const dateStr = day.toISOString().split('T')[0];
                  const isStart = isAppointmentStart(dateStr, time);
                  const isCovered = getCoveredSlots[dateStr]?.has(time);
                  const isPast = isPastSlot(day, time);

                  // Si este slot está cubierto por un rowSpan de un turno anterior, no renderizar
                  if (isCovered) {
                    return null;
                  }

                  // Si hay un turno que inicia en este slot, mostrarlo con rowSpan
                  if (isStart) {
                    const appointment = getAppointmentAtSlot(dateStr, time)!;
                    const slotSpan = getAppointmentSlotSpan(appointment);
                    return (
                      <td
                        key={`${dateStr}-${time}`}
                        rowSpan={slotSpan}
                        className="p-1 border border-gray-200 align-top"
                      >
                        <div
                          className={`h-full p-2 rounded cursor-pointer transition-all hover:shadow-md ${
                            appointment.status === 'completed'
                              ? 'bg-green-100 border-l-4 border-green-500'
                              : appointment.status === 'cancelled'
                              ? 'bg-red-100 border-l-4 border-red-500'
                              : 'bg-blue-100 border-l-4 border-blue-500'
                          }`}
                          onClick={() => handleEditAppointment(appointment)}
                          style={{ minHeight: `${slotSpan * 32 - 8}px` }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm truncate">
                              {appointment.patient_name}
                            </span>
                            <span className="text-xs text-gray-600 ml-1">
                              {appointment.duration_minutes}m
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 truncate">
                            {SERVICE_TYPE_LABELS[appointment.service_type]}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {appointment.owner_name}
                          </p>
                          {/* Acciones rápidas */}
                          {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                            <div className="flex gap-1 mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCompleteConfirm(appointment);
                                }}
                                className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                title="Completar"
                              >
                                <Check className="h-3 w-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCancelConfirm(appointment);
                                }}
                                className="p-1 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                                title="Cancelar turno"
                              >
                                <Ban className="h-3 w-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirm(appointment);
                                }}
                                className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  }

                  // Slot vacío - clickeable para crear turno (incluso si ya pasó,
                  // por si se cubre un turno cancelado sobre la hora)
                  return (
                    <td
                      key={`${dateStr}-${time}`}
                      className={`p-0 border border-gray-200 h-8 transition-colors cursor-pointer ${
                        isPast ? 'bg-gray-100 hover:bg-amber-100' : 'hover:bg-green-100'
                      } ${isToday(day) && !isPast ? 'bg-blue-50/50' : ''}`}
                      onClick={() => handleNewAppointment(dateStr, time)}
                      title={isPast ? `Horario pasado — podés asignar igual a las ${time}` : `Crear turno a las ${time}`}
                    >
                      <div className="h-full w-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <Plus className={`h-4 w-4 ${isPast ? 'text-amber-600' : 'text-green-600'}`} />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Renderizar tabla para vista de lista
  const renderListView = () => (
    <div className="card overflow-hidden">
      {filteredAppointments.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Turno</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mascota / Dueño</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAppointments.map((apt) => (
                <tr key={apt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-pink-100 rounded-lg flex items-center justify-center">
                        <Scissors className="h-5 w-5 text-pink-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {(() => {
                            // Parse date as local to avoid timezone issues
                            const [year, month, day] = apt.scheduled_date.split('-').map(Number);
                            const localDate = new Date(year, month - 1, day);
                            return localDate.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
                          })()}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {apt.scheduled_time.slice(0, 5)} ({apt.duration_minutes}min)
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{apt.patient_name}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {apt.owner_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                      {SERVICE_TYPE_LABELS[apt.service_type]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[apt.status]}`}>
                      {STATUS_LABELS[apt.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex justify-end gap-1">
                      {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleEditAppointment(apt)} title="Editar">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setCompleteConfirm(apt)} title="Completar" className="text-green-600 hover:bg-green-50">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setCancelConfirm(apt)} title="Cancelar turno" className="text-orange-600 hover:bg-orange-50">
                            <Ban className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(apt)} title="Eliminar" className="text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">No hay turnos para mostrar</div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Peluquería</h1>
          <p className="text-gray-600">Gestión de turnos de peluquería</p>
        </div>
        <Button onClick={() => handleNewAppointment()}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Turno
        </Button>
      </div>

      {/* Filtros y Controles */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Navegación de fechas */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigateCalendar(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigateCalendar(1)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
              <Button variant="secondary" size="sm" onClick={goToToday}>
                Hoy
              </Button>
            </div>
            <span className="font-medium text-gray-700">
              {viewMode === 'slots'
                ? `${days[0]?.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}`
                : currentDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
            </span>
          </div>

          <div className="flex items-center gap-4">
            {/* Filtro de estado (solo en vista lista) */}
            {viewMode === 'list' && (
              <div className="w-48">
                <Select
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as GroomingStatus | '')}
                />
              </div>
            )}

            {/* Selector de vista */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-md">
              <button
                onClick={() => setViewMode('slots')}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  viewMode === 'slots'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Vista de horarios"
              >
                <Clock className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Vista de lista"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Leyenda para vista de slots */}
      {viewMode === 'slots' && (
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-100 border-l-4 border-blue-500 rounded"></div>
            <span className="text-gray-600">Pendiente</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 border-l-4 border-green-500 rounded"></div>
            <span className="text-gray-600">Completado</span>
          </div>
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-green-600" />
            <span className="text-gray-600">Click en slot vacío para crear turno</span>
          </div>
        </div>
      )}

      {/* Contenido según vista */}
      {isLoading ? (
        <div className="card text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Cargando turnos...</p>
        </div>
      ) : viewMode === 'list' ? (
        renderListView()
      ) : (
        renderSlotsView()
      )}

      {/* Modal de crear/editar turno */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAppointment(null);
          setSelectedDate(null);
          setSelectedTime(null);
        }}
        title={editingAppointment ? 'Editar Turno' : 'Nuevo Turno'}
        size="lg"
      >
        <GroomingAppointmentForm
          appointment={editingAppointment}
          preselectedDate={selectedDate || undefined}
          preselectedTime={selectedTime || undefined}
          onSuccess={() => {
            setIsModalOpen(false);
            setEditingAppointment(null);
            setSelectedDate(null);
            setSelectedTime(null);
          }}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingAppointment(null);
            setSelectedDate(null);
            setSelectedTime(null);
          }}
        />
      </Modal>

      {/* Modal de confirmación de completar */}
      <Modal
        isOpen={!!completeConfirm}
        onClose={() => setCompleteConfirm(null)}
        title="Completar Turno"
        size="sm"
      >
        {completeConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Dar por realizado el turno de <strong>{completeConfirm.patient_name}</strong> del{' '}
              {(() => {
                const [year, month, day] = completeConfirm.scheduled_date.split('-').map(Number);
                return new Date(year, month - 1, day).toLocaleDateString('es-AR');
              })()} a las {completeConfirm.scheduled_time.slice(0, 5)}?
            </p>
            <p className="text-sm text-green-700">
              Se va a generar una comanda con el servicio para poder cobrarla.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCompleteConfirm(null)}>
                Volver
              </Button>
              <Button onClick={handleComplete} loading={completeAppointment.isPending}>
                Completar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmación de eliminar */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Eliminar Turno"
        size="sm"
      >
        {deleteConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de eliminar el turno de <strong>{deleteConfirm.patient_name}</strong> del{' '}
              {(() => {
                const [year, month, day] = deleteConfirm.scheduled_date.split('-').map(Number);
                return new Date(year, month - 1, day).toLocaleDateString('es-AR');
              })()} a las {deleteConfirm.scheduled_time.slice(0, 5)}?
            </p>
            <p className="text-sm text-red-600">
              Esta acción no se puede deshacer.
            </p>
            {/*
              Borrar saca el turno de la base, asi que tampoco queda en el reporte de
              cancelaciones por cliente. Si el cliente avisó que no viene, lo correcto es
              cancelarlo, no borrarlo.
            */}
            <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
              Si el cliente avisó que no viene, conviene <strong>cancelarlo</strong> en vez de
              eliminarlo: eliminado no queda registrado en el reporte de cancelaciones por
              cliente.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={deleteAppointment.isPending}>
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmación de cancelar */}
      <Modal
        isOpen={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        title="Cancelar Turno"
        size="sm"
      >
        {cancelConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de cancelar el turno de <strong>{cancelConfirm.patient_name}</strong> del{' '}
              {(() => {
                const [year, month, day] = cancelConfirm.scheduled_date.split('-').map(Number);
                return new Date(year, month - 1, day).toLocaleDateString('es-AR');
              })()} a las {cancelConfirm.scheduled_time.slice(0, 5)}?
            </p>
            <p className="text-sm text-orange-600">
              El turno quedará marcado como cancelado. Esta cancelación se registrará para el cliente.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCancelConfirm(null)}>
                Volver
              </Button>
              <Button variant="danger" onClick={handleCancel} loading={cancelAppointment.isPending}>
                Cancelar Turno
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de comanda creada */}
      <Modal
        isOpen={!!completeResult}
        onClose={() => setCompleteResult(null)}
        title="Turno Completado"
        size="sm"
        closeOnBackdropClick={false}
      >
        {completeResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <div className="flex-shrink-0">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">
                  Mascota presente - Servicio completado
                </p>
                <p className="text-sm text-green-600">
                  {completeResult.appointment.patient_name}
                </p>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">Comanda Creada</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Se generó la comanda #{completeResult.serviceOrderId} lista para editar.
              </p>
              <p className="text-lg font-bold text-blue-800">
                Total: {formatPrice(completeResult.total)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Puedes agregar productos adicionales (shampoo especial, etc.) o ajustar el precio antes de cobrar.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setCompleteResult(null)}>
                Cerrar
              </Button>
              <Button onClick={handleGoToServiceOrder}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Ir a Comanda
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
